import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { TextField } from '@/components/ui/TextField';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useFantasySeasons } from '@/features/fantasy/hooks';
import {
  useCreateFriendLeague,
  useJoinFriendLeague,
  useMyFriendLeagues,
} from '@/features/games/friendLeagues';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { FriendLeague } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

function LeagueRow({ league, onPress }: { league: FriendLeague; onPress: () => void }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      className="mb-2.5 flex-row items-center gap-3 rounded-2xl border border-line bg-surface px-4 py-3.5 active:bg-surface-2"
    >
      <View className="h-10 w-10 items-center justify-center rounded-full bg-volt-glass">
        <Ionicons name={league.is_duel ? 'flash' : 'people'} size={20} color={palette.voltBright} />
      </View>
      <View className="flex-1">
        <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
          {league.name}
        </AppText>
        <AppText variant="micro" className="text-soft">
          {league.is_duel
            ? t('friendLeagues.duelTag')
            : t('friendLeagues.memberCount', {
                n: formatNumber(league.member_count, numeralOptions),
              })}
        </AppText>
      </View>
      <View className="items-end">
        <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
          {formatNumber(league.my_score, numeralOptions)}
        </AppText>
        <AppText variant="micro" className="text-muted">
          {t('friendLeagues.pts')}
        </AppText>
      </View>
      <Ionicons name="chevron-forward" size={16} color={palette.muted} />
    </Pressable>
  );
}

export default function FriendLeaguesScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const { userId } = useAuth();
  const toast = useToast();
  const howto = useHowToPlay('friendLeagues');
  const seasons = useFantasySeasons();
  const wcSeason =
    (seasons.data ?? []).find((s) => (s.name ?? '').toLowerCase().includes('world cup')) ??
    seasons.data?.[0];

  const leagues = useMyFriendLeagues();
  const create = useCreateFriendLeague();
  const join = useJoinFriendLeague();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  // A deep-linked invite (tashkeela://league/<CODE>) arrives as a `join` param.
  // Pre-fill the code, highlight the Join card and scroll to it - we never
  // auto-submit, the user still taps Join (same safety posture as referral).
  const { join: joinParam } = useLocalSearchParams<{ join?: string }>();
  const scrollRef = useRef<ScrollView>(null);
  const joinCardY = useRef(0);
  const [highlightJoin, setHighlightJoin] = useState(false);
  useEffect(() => {
    const incoming = (Array.isArray(joinParam) ? joinParam[0] : joinParam)?.trim().toUpperCase();
    if (!incoming) return;
    // Apply on the next tick (not synchronously in the effect body) and after
    // layout has measured the Join card's offset, then scroll it into view.
    const apply = setTimeout(() => {
      setCode(incoming);
      setHighlightJoin(true);
      scrollRef.current?.scrollTo({ y: Math.max(0, joinCardY.current - 8), animated: true });
    }, 350);
    const clear = setTimeout(() => setHighlightJoin(false), 2600);
    return () => {
      clearTimeout(apply);
      clearTimeout(clear);
    };
  }, [joinParam]);

  const onCreate = (isDuel: boolean) => {
    if (name.trim().length < 1) {
      toast.show({ message: t('friendLeagues.nameRequired'), tone: 'error' });
      return;
    }
    create.mutate(
      { name: name.trim(), isDuel, seasonId: wcSeason?.id },
      {
        onSuccess: (res) => {
          setName('');
          toast.show({ message: t('friendLeagues.created'), tone: 'success' });
          router.push(`/games/friend-league/${res.id}` as never);
        },
        onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
      },
    );
  };

  const onJoin = () => {
    if (code.trim().length < 3) {
      toast.show({ message: t('friendLeagues.codeRequired'), tone: 'error' });
      return;
    }
    join.mutate(code.trim(), {
      onSuccess: (res) => {
        setCode('');
        toast.show({ message: t('friendLeagues.joined', { name: res.name }), tone: 'success' });
        router.push(`/games/friend-league/${res.id}` as never);
      },
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });
  };

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="friendLeagues" icon="people" />
      <BackHeader title={t('friendLeagues.title')} right={<HowToPlayButton onPress={howto.open} />} />
      <ScrollView ref={scrollRef} contentContainerStyle={{ paddingBottom: 40, gap: 14 }}>
        <AppText variant="caption" className="mx-4 mt-1 text-center text-soft">
          {t('friendLeagues.intro')}
        </AppText>

        {/* Create */}
        <Card className="mx-4 gap-3 px-4 py-4">
          <AppText variant="caption" weight="bold" className="uppercase tracking-wide text-soft">
            {t('friendLeagues.createTitle')}
          </AppText>
          <TextField
            placeholder={t('friendLeagues.namePlaceholder')}
            value={name}
            onChangeText={setName}
            maxLength={40}
          />
          <View className="flex-row gap-2.5">
            <View className="flex-1">
              <Button
                label={t('friendLeagues.createLeague')}
                loading={create.isPending}
                disabled={!userId}
                onPress={() => onCreate(false)}
              />
            </View>
            <Button
              label={t('friendLeagues.createDuel')}
              variant="secondary"
              disabled={!userId || create.isPending}
              onPress={() => onCreate(true)}
            />
          </View>
        </Card>

        {/* Join */}
        <Card
          onLayout={(e) => {
            joinCardY.current = e.nativeEvent.layout.y;
          }}
          className={`mx-4 gap-3 px-4 py-4 ${highlightJoin ? 'border-volt-line bg-volt-glass' : ''}`}
        >
          <AppText variant="caption" weight="bold" className="uppercase tracking-wide text-soft">
            {t('friendLeagues.joinTitle')}
          </AppText>
          {highlightJoin ? (
            <AppText variant="micro" className="text-volt-bright">
              {t('friendLeagues.invitePrefilled')}
            </AppText>
          ) : null}
          <View className="flex-row items-end gap-2.5">
            <View className="flex-1">
              <TextField
                placeholder={t('friendLeagues.codePlaceholder')}
                value={code}
                onChangeText={(v) => setCode(v.toUpperCase())}
                autoCapitalize="characters"
                autoCorrect={false}
                maxLength={12}
              />
            </View>
            <Button
              label={t('friendLeagues.join')}
              loading={join.isPending}
              disabled={!userId}
              onPress={onJoin}
            />
          </View>
        </Card>

        {/* My leagues */}
        <View className="mx-4">
          <AppText variant="caption" weight="bold" className="mb-2 uppercase tracking-wide text-soft">
            {t('friendLeagues.mine')}
          </AppText>
          {leagues.isPending ? (
            <View className="gap-2.5">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </View>
          ) : (leagues.data ?? []).length === 0 ? (
            <EmptyState
              icon="people-outline"
              title={t('friendLeagues.emptyTitle')}
              body={t('friendLeagues.emptyBody')}
            />
          ) : (
            (leagues.data ?? []).map((league) => (
              <LeagueRow
                key={league.id}
                league={league}
                onPress={() => router.push(`/games/friend-league/${league.id}` as never)}
              />
            ))
          )}
        </View>

        {!userId ? (
          <AppText variant="micro" className="mx-4 text-center text-muted">
            {t('friendLeagues.signInHint')}
          </AppText>
        ) : null}
      </ScrollView>
    </Screen>
  );
}
